import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { contractApi, api } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

// Import extracted components
import {
  ContractHeader,
  ContractClientCard,
  ContractDocumentsCard,
  ContractValidationCard,
  ContractProjectCard,
  ValidationModal,
  AppaltoModal
} from '../components/contracts';
import { VatRateCard } from '../components/shared/VatRateCard';

/**
 * Documenti richiesti per contratto.
 * 
 * NOTA: "preventivo_firmato" è stato rimosso dai documenti obbligatori
 * perché il Contratto Appalto V2 con Allegato A dinamico lo sostituisce/assorbe
 * come documento economico e tecnico vincolante.
 * 
 * "progetto_demolizioni" è stato spostato tra gli opzionali.
 */
const REQUIRED_DOCUMENTS = [
  'contratto_firmato', 
  'documento_identita', 
  'visura_catastale', 
  'progetto_stato_luoghi', 
  'progetto_stato_progetto'
];

const ContractDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, isProjectManager, isAdministration } = useAuth();
  const fileInputRef = useRef(null);
  
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [documentsFromAPI, setDocumentsFromAPI] = useState([]);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationType, setValidationType] = useState('');
  const [validationApproved, setValidationApproved] = useState(null);
  const [validationNotes, setValidationNotes] = useState('');
  const [selectedDocType, setSelectedDocType] = useState('contratto_firmato');
  const [docNotes, setDocNotes] = useState('');

  // Generazione Contratto Appalto
  const [showAppaltoModal, setShowAppaltoModal] = useState(false);
  const [generatingAppalto, setGeneratingAppalto] = useState(false);
  const [calculatedEndDate, setCalculatedEndDate] = useState(null);
  const [paymentTiers, setPaymentTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  
  // Feature Flag: Document Management
  const [documentsFeatureEnabled, setDocumentsFeatureEnabled] = useState(null); // null = loading, true/false = stato
  
  // V2: selected_work_sections removed - Allegato A is auto-generated from contracted items
  const [appaltoForm, setAppaltoForm] = useState({
    contract_date: new Date().toISOString().split('T')[0],
    place_of_signature: 'Bari',
    total_net_amount: 0,
    vat_rate: 10,
    planned_start_date: '',
    working_days_duration: 60,
    payment_installments: [
      { label: 'Acconto alla firma', percentage: 10, amount: 0, due_text: 'Alla firma del contratto' },
      { label: 'Inizio lavori', percentage: 20, amount: 0, due_text: 'All\'inizio dei lavori' },
      { label: 'SAL 1', percentage: 30, amount: 0, due_text: 'Al raggiungimento del primo stato avanzamento' },
      { label: 'SAL 2', percentage: 30, amount: 0, due_text: 'Al raggiungimento del secondo stato avanzamento' },
      { label: 'Saldo finale', percentage: 10, amount: 0, due_text: 'Saldo a fine lavori' },
    ],
    has_capitolato: true,  // Always true in V2
    has_economic_schedule: false,
    has_timeline: false,
    has_technical_drawings: false,
    customer_is_consumer: true,
  });

  const canValidateComputo = isAdmin || isProjectManager;
  const canValidateAdminDocs = isAdmin || isProjectManager || isAdministration;
  const canUpload = contract?.created_by === user?.id || isAdmin || isAdministration;
  
  const uploadedDocTypes = new Set(contract?.documents?.map(d => d.document_type) || []);
  const missingDocs = REQUIRED_DOCUMENTS.filter(d => !uploadedDocTypes.has(d));
  const allRequiredUploaded = missingDocs.length === 0;
  
  const canSubmit = canUpload && allRequiredUploaded && 
    ['bozza', 'in_attesa_firma', 'documenti_mancanti'].includes(contract?.status);
  
  // Semplificazione: pulsanti validazione visibili sempre (se contratto non già approvato)
  // I permessi utente controllano chi può validare
  // I requisiti documenti controllano quando il pulsante admin_docs è abilitato
  const canShowValidationButtons = contract?.status !== 'approvato';

  useEffect(() => {
    loadContract();
    loadDocumentsFromAPI();
    checkDocumentsFeatureStatus();
  }, [id]);

  const checkDocumentsFeatureStatus = async () => {
    try {
      const res = await api.get('/documents/feature-status');
      setDocumentsFeatureEnabled(res.data.enabled);
    } catch (error) {
      // Se l'endpoint fallisce, assumiamo che la feature sia disabilitata per sicurezza
      console.warn('Could not check document feature status:', error);
      setDocumentsFeatureEnabled(false);
    }
  };

  const loadContract = async () => {
    try {
      setLoading(true);
      const res = await contractApi.getOne(id);
      setContract(res.data);
    } catch (error) {
      console.error('Error loading contract:', error);
      toast.error('Errore nel caricamento del contratto');
      navigate('/contracts');
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentsFromAPI = async () => {
    try {
      // Carica documenti dalla nuova API crm_documents
      const res = await api.get(`/contracts/${id}/documents?include_archived=true`);
      setDocumentsFromAPI(res.data);
    } catch (error) {
      console.error('Error loading documents from API:', error);
      // Fallback: usa documenti dal contratto
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File troppo grande (max 10MB)');
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo file non supportato. Usa PDF o immagini.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', selectedDocType);
      if (docNotes) formData.append('notes', docNotes);

      await contractApi.uploadDocument(id, formData);
      toast.success('Documento caricato con successo');
      loadContract();
      loadDocumentsFromAPI();
      setDocNotes('');
    } catch (error) {
      console.error('Error uploading:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      await api.delete(`/documents/${documentId}`);
      toast.success('Documento archiviato');
      loadContract();
      loadDocumentsFromAPI();
    } catch (error) {
      toast.error('Errore nell\'archiviazione');
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.id}/download`, {
        responseType: 'blob'
      });
      
      // Crea link per download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename || 'documento');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download completato');
    } catch (error) {
      // Gestione specifica per errore 410 (file orfano / non disponibile)
      if (error.response?.status === 410) {
        // Prova a leggere il messaggio dal blob di errore
        try {
          const errorText = await error.response.data.text();
          const errorData = JSON.parse(errorText);
          if (errorData?.detail?.error === 'FILE_MISSING') {
            toast.error(
              'File non disponibile: il documento risulta registrato nel CRM, ma il file fisico non è più presente nello storage. Potrebbe essere necessario ricaricarlo.',
              { duration: 8000 }
            );
            return;
          }
        } catch (e) {
          // Fallback se non riesce a parsare
        }
        toast.error('Documento non più disponibile nello storage', { duration: 5000 });
        return;
      }
      
      if (error.response?.status === 403) {
        toast.error('Non hai i permessi per scaricare questo documento');
      } else if (error.response?.status === 404) {
        toast.error('Documento non trovato');
      } else {
        toast.error('Errore durante il download');
      }
      throw error;
    }
  };

  const handleValidateDocument = async (documentId, status) => {
    try {
      await api.post(`/documents/${documentId}/validate?status=${status}`);
      loadDocumentsFromAPI();
    } catch (error) {
      toast.error('Errore durante la validazione');
      throw error;
    }
  };

  const handleSubmitForValidation = async () => {
    try {
      await contractApi.submitForValidation(id);
      toast.success('Contratto inviato per validazione');
      loadContract();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio');
    }
  };

  const openValidationModal = (type) => {
    setValidationType(type);
    setValidationApproved(null);
    setValidationNotes('');
    setShowValidationModal(true);
  };

  const handleValidate = async () => {
    if (validationApproved === null) {
      toast.error('Seleziona un esito');
      return;
    }

    if (!validationApproved && !validationNotes) {
      toast.error('Inserisci le note per la richiesta di integrazioni');
      return;
    }

    try {
      if (validationType === 'computo') {
        await contractApi.validateComputo(id, validationApproved, validationNotes || null);
        toast.success('Validazione Computo Metrico completata');
      } else {
        await contractApi.validateAdminDocs(id, validationApproved, validationNotes || null);
        toast.success('Validazione Documenti Amministrativi completata');
      }
      setShowValidationModal(false);
      loadContract();
    } catch (error) {
      toast.error('Errore nella validazione');
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await contractApi.getPdf(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Contratto_${contract.contract_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.info('Nessun PDF generato. Usa "Genera Contratto Appalto" per creare il documento.');
      } else {
        toast.error('Errore nel download');
      }
    }
  };

  const handleDeleteContract = async () => {
    if (!window.confirm('Sei sicuro di voler eliminare questo contratto in bozza?')) return;
    
    try {
      await contractApi.delete(id);
      toast.success('Contratto eliminato');
      navigate('/contracts');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione del contratto');
    }
  };

  // === GENERAZIONE CONTRATTO APPALTO ===
  const findMatchingTier = (tiers, amount) => {
    if (!tiers || tiers.length === 0) return null;
    const sorted = [...tiers].sort((a, b) => a.min_amount - b.min_amount);
    for (let i = sorted.length - 1; i >= 0; i--) {
      const tier = sorted[i];
      if (amount >= tier.min_amount) {
        if (!tier.max_amount || amount <= tier.max_amount) {
          return tier;
        }
      }
    }
    return sorted[0];
  };

  const getTriggerText = (milestone, isLastItem = false) => {
    const isSaldo = milestone.name?.toLowerCase().includes('saldo') || isLastItem;
    if (isSaldo) return 'Saldo fine lavori';
    
    switch (milestone.trigger_type) {
      case 'firma_contratto': return 'Alla firma del contratto';
      case 'inizio_lavori': return 'All\'inizio dei lavori';
      case 'sal_percentuale': return `Al raggiungimento del ${milestone.trigger_sal_percentage || 50}% dei lavori`;
      case 'fine_lavori': return 'Saldo fine lavori';
      default: return milestone.name;
    }
  };

  const openAppaltoModal = async () => {
    try {
      const [settingsRes, tiersRes] = await Promise.all([
        contractApi.getTemplateSettings(),
        fetch(`/api/payment-tiers`, { withCredentials: true }).then(r => r.json())
      ]);
      
      setPaymentTiers(tiersRes || []);
      
      const netAmount = contract.contract_amount || 0;
      const matchingTier = findMatchingTier(tiersRes || [], netAmount);
      setSelectedTier(matchingTier);
      
      let installments;
      if (matchingTier && matchingTier.milestones?.length > 0) {
        const sortedMilestones = matchingTier.milestones.sort((a, b) => a.order - b.order);
        installments = sortedMilestones.map((m, idx) => ({
          label: m.name,
          percentage: m.percentage,
          amount: Math.round((netAmount * m.percentage / 100) * 100) / 100,
          due_text: getTriggerText(m, idx === sortedMilestones.length - 1)
        }));
      } else {
        installments = [
          { label: 'Acconto alla firma', percentage: 30, amount: Math.round((netAmount * 0.3) * 100) / 100, due_text: 'Alla firma del contratto' },
          { label: 'SAL 1', percentage: 30, amount: Math.round((netAmount * 0.3) * 100) / 100, due_text: 'Al raggiungimento del 30% dei lavori' },
          { label: 'SAL 2', percentage: 30, amount: Math.round((netAmount * 0.3) * 100) / 100, due_text: 'Al raggiungimento del 60% dei lavori' },
          { label: 'Saldo finale', percentage: 10, amount: Math.round((netAmount * 0.1) * 100) / 100, due_text: 'A fine lavori' },
        ];
      }
      
      // V2: No more selected_work_sections - Allegato A is auto-generated
      // FASE 0 IVA: usa vat_rate persistito sul contratto (fallback 10%)
      setAppaltoForm(prev => ({
        ...prev,
        total_net_amount: netAmount,
        vat_rate: contract?.vat_rate ?? prev.vat_rate ?? 10,
        payment_installments: installments
      }));
      
      setShowAppaltoModal(true);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Errore nel caricamento delle impostazioni');
    }
  };

  const calculateEndDate = (startDate, workingDays) => {
    if (!startDate) return;
    
    const start = new Date(startDate);
    let daysAdded = 0;
    const current = new Date(start);
    
    while (daysAdded < workingDays) {
      current.setDate(current.getDate() + 1);
      const dayOfWeek = current.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        daysAdded++;
      }
    }
    
    setCalculatedEndDate(current.toLocaleDateString('it-IT'));
  };

  const handleUpdateInstallment = (idx, field, value) => {
    setAppaltoForm(prev => {
      const newInstallments = [...prev.payment_installments];
      newInstallments[idx] = { ...newInstallments[idx], [field]: value };
      
      if (field === 'percentage') {
        newInstallments[idx].amount = Math.round((prev.total_net_amount * value / 100) * 100) / 100;
      }
      
      return { ...prev, payment_installments: newInstallments };
    });
  };

  const handleRemoveInstallment = (idx) => {
    setAppaltoForm(prev => ({
      ...prev,
      payment_installments: prev.payment_installments.filter((_, i) => i !== idx)
    }));
  };

  const handleAddInstallment = () => {
    setAppaltoForm(prev => ({
      ...prev,
      payment_installments: [
        ...prev.payment_installments,
        { label: 'Nuova rata', percentage: 0, amount: 0, due_text: '' }
      ]
    }));
  };

  const handleGenerateAppalto = async () => {
    const totalPercentage = appaltoForm.payment_installments.reduce((sum, i) => sum + i.percentage, 0);
    if (totalPercentage !== 100) {
      toast.error('Le percentuali devono sommare a 100%');
      return;
    }

    setGeneratingAppalto(true);
    try {
      await contractApi.generateAppalto(id, appaltoForm);
      toast.success('Contratto di Appalto generato!');
      setShowAppaltoModal(false);
      loadContract();
    } catch (error) {
      console.error('Error generating appalto:', error);
      toast.error(error.response?.data?.detail || 'Errore nella generazione');
    } finally {
      setGeneratingAppalto(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF7819]" />
      </div>
    );
  }

  if (!contract) return null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contract-detail-page">
      {/* Header */}
      <ContractHeader
        contract={contract}
        onBack={() => navigate('/contracts')}
        onDownloadPdf={handleDownloadPdf}
        onOpenAppaltoModal={openAppaltoModal}
        onDelete={handleDeleteContract}
        generatingAppalto={generatingAppalto}
        isAdmin={isAdmin}
      />

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client Card */}
          <ContractClientCard contract={contract} />

          {/* Documents Card - Solo se feature abilitata (Preview) */}
          {documentsFeatureEnabled === true && (
            <ContractDocumentsCard
              contract={{...contract, documentsFromAPI}}
              contractId={id}
              canUpload={canUpload}
              canValidate={canValidateAdminDocs}
              uploading={uploading}
              selectedDocType={selectedDocType}
              setSelectedDocType={setSelectedDocType}
              docNotes={docNotes}
              setDocNotes={setDocNotes}
              fileInputRef={fileInputRef}
              onFileUpload={handleFileUpload}
              onDeleteDocument={handleDeleteDocument}
              onDownloadDocument={handleDownloadDocument}
              onValidateDocument={handleValidateDocument}
              onRefreshDocuments={loadDocumentsFromAPI}
            />
          )}
          
          {/* Messaggio informativo se Documents disabilitato (Production) */}
          {documentsFeatureEnabled === false && (
            <Card className="card-abitalo border-amber-200 bg-amber-50/50">
              <CardContent className="py-6">
                <div className="flex items-center gap-3 text-amber-700">
                  <AlertTriangle className="h-5 w-5" />
                  <div>
                    <p className="font-medium">Gestione Documenti non disponibile</p>
                    <p className="text-sm text-amber-600 mt-1">
                      La funzionalità di upload documenti sarà attivata prossimamente 
                      dopo la configurazione dello storage cloud sicuro.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        {/* Right Column - Sidebar (stack verticale content-driven: self-start evita che il grid
            item si stiri all'altezza della colonna sinistra, gonfiando la card IVA con h-full) */}
        <div className="space-y-6 self-start">
          {/* Project Card - Collegamento Progetto/Cantiere */}
          <ContractProjectCard
            contract={contract}
            contractId={id}
            onProjectLinked={(projectId, projectNumber) => {
              // Aggiorna lo stato del contratto localmente
              setContract(prev => ({ ...prev, project_id: projectId }));
              toast.success(`Collegato al progetto ${projectNumber}`);
            }}
          />

          {/* VAT Rate Card - FASE 0 IVA */}
          <VatRateCard
            vatRate={contract?.vat_rate ?? 10}
            contractAmount={contract?.contract_amount ?? 0}
            needsReview={contract?._vat_needs_review === true}
            reviewReasons={contract?._vat_review_reasons || []}
            canEdit={isAdmin || isAdministration}
            source="contract"
            onSave={async (newRate) => {
              const response = await contractApi.updateVatRate(id, newRate);
              setContract(response.data);
            }}
          />

          {/* Validation Card */}
          <ContractValidationCard
            contract={contract}
            contractId={id}
            canValidateComputo={canValidateComputo}
            canValidateAdminDocs={canValidateAdminDocs}
            canShowValidationButtons={canShowValidationButtons}
            onOpenValidationModal={openValidationModal}
          />

          {/* Info Card */}
          <Card className="card-abitalo">
            <CardHeader>
              <CardTitle className="text-lg">Informazioni</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-[#737373]">Creato il</span>
                <span>{formatDate(contract.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#737373]">Ultimo aggiornamento</span>
                <span>{formatDate(contract.updated_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#737373]">Preventivo</span>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 h-auto text-[#FF7819]"
                  onClick={() => navigate(`/estimates/${contract.estimate_id}`)}
                >
                  Visualizza
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Validation Modal */}
      <ValidationModal
        open={showValidationModal}
        onOpenChange={setShowValidationModal}
        validationType={validationType}
        validationApproved={validationApproved}
        setValidationApproved={setValidationApproved}
        validationNotes={validationNotes}
        setValidationNotes={setValidationNotes}
        onValidate={handleValidate}
      />

      {/* Appalto Modal */}
      <AppaltoModal
        open={showAppaltoModal}
        onOpenChange={setShowAppaltoModal}
        appaltoForm={appaltoForm}
        setAppaltoForm={setAppaltoForm}
        calculatedEndDate={calculatedEndDate}
        selectedTier={selectedTier}
        generatingAppalto={generatingAppalto}
        onGenerate={handleGenerateAppalto}
        onUpdateInstallment={handleUpdateInstallment}
        onRemoveInstallment={handleRemoveInstallment}
        onAddInstallment={handleAddInstallment}
        calculateEndDate={calculateEndDate}
        contractId={id}
        api={api}
      />
    </div>
  );
};

export default ContractDetailPage;
